import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    Menu,
    Bell,
    User,
    Settings,
    LogOut,
    Home,
    Users,
    Globe,
    FileText,
    Shield,
    Wallet,
    History,
    Star,
    X,
    User2,
    Settings2,
    Clock,
    ChevronDown,
    MessageCircle,
    StarIcon,
    Verified,
    Lock,
    FileCheck,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { MdReviews } from "react-icons/md";
import SocialMediaLinks from "../components/ui/SocialMediaLinks";

// Fix the ChartAreaIcon import
import { BarChart as ChartAreaIcon } from "lucide-react";
import { useAppDispatch, useAppSelector, LogoutUser, LogoutManually } from "../redux";
import SidebarComponent from "../modules/client/components/ClientProfileComponents/SidebarComponent";
import { RoleEnum } from "../modules";
import LanguageSelector from "../components/LanguageSelector";

const LANGUAGES = [
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
    { code: "fr", name: "Français" },
    { code: "de", name: "Deutsch" },
];

const StoreLinks = () => (
    <div className="grid grid-cols-2 gap-3 pt-4">
        <a href="https://play.google.com/store/apps/details?id=com.kokobeo" className="flex justify-center">
            <img
                src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
                alt="Get it on Google Play"
                className="w-full max-w-[140px] h-auto rounded-lg hover:opacity-90 transition-opacity"
            />
        </a>
        <a href="https://apps.apple.com/app/kokobeo" className="flex justify-center">
            <img
                src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
                alt="Download on App Store"
                className="w-full max-w-[140px] h-auto rounded-lg hover:opacity-90 transition-opacity"
            />
        </a>
    </div>
);

const SocialIcons = () => (
    <SocialMediaLinks containerSize="w-7 h-7"  className="flex justify-center space-x-4 pt-4 border-t border-gray-100" iconSize="h-4 w-4 sm:h-5 sm:w-5" />
);

const SharedHeader5 = () => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { unreadCount } = useAppSelector((state) => state.userNotifications);
    const { user } = useAppSelector((state) => state.auth);

    const MENU_ITEMS = [
        {
            icon: <Home className="h-5 w-5" />,
            label: t("sidebar.dashboard"),
            path: "/clients",
            align: "left",
            rightIcon: <Home className="h-4 w-4 text-gray-400" />
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("sidebar.postJobLocal"),
            path: "/clients/postajob",
            align: "left",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("sidebar.postJobInternational"),
            path: "/clients/postajob/international",
            align: "left",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("sidebar.findLocalProfessionals"),
            path: "/local-professionals",
            align: "left",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("sidebar.findInternationalProfessionals"),
            path: "/professional-international",
            align: "left",
        },
        {
            icon: <User2 className="h-5 w-5" />,
            label: t("sidebar.myProfile"),
            path: "/client/profile",
            align: "left",
        },
        {
            icon: <User className="h-5 w-5" />,
            label: t("sidebar.profileSettings"),
            path: "/client/profilesettings",
            align: "left",
        },
        {
            icon: <FileCheck className="h-5 w-5" />,
            label: t("sidebar.myOrders"),
            path: "/client/myorders",
            align: "left",
        },
        {
            icon: <Bell className="h-5 w-5" />,
            label: t("sidebar.myOffers"),
            path: "/client/myoffers",
            align: "left",
        },
        {
            icon: <StarIcon className="h-5 w-5" />,
            label: t("sidebar.favorites"),
            path: "/client/favorites",
            align: "left",
        },
        {
            icon: <MessageCircle className="h-5 w-5" />,
            label: t("sidebar.messageOrVideoCall"),
            path: "/client/MessageAndVideoCall",
            align: "left",
        },
        {
            icon: <User className="h-5 w-5" />,
            label: t("sidebar.report"),
            path: "/client/reports",
            align: "left",
        },
        {
            icon: <Wallet className="h-5 w-5" />,
            label: t("sidebar.wallet"),
            path: "/client/Wallet",
            align: "left",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("sidebar.myInvoices"),
            path: "/client/invoices",
            align: "left",
        },
        {
            icon: <ChartAreaIcon className="h-5 w-5" />,
            label: t("sidebar.progress"),
            path: "/client/progress",
            align: "left",
        },
        {
            icon: <MdReviews className="h-5 w-5" />,
            label: t("sidebar.reviews"),
            path: "/client/reviews",
            align: "left",
        },
        {
            icon: <Shield className="h-5 w-5" />,
            label: t("sidebar.supportTickets"),
            path: "/client/support",
            align: "left",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("sidebar.contactUs"),
            path: "/menu/contact",
            align: "left",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("sidebar.refundRequest"),
            path: "/client/refund-requests",
            align: "left",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("sidebar.verifyAccount"),
            path: "/verify",
            align: "left",
        },
        { icon: <Lock className="h-5 w-5" />, label: t("sidebar.changePassword"), path: "/changepassword", align: "left" },
        {
            icon: <LogOut className="h-5 w-5" />,
            label: t("sidebar.logOut"),
            path: "/logout",
            align: "left",
        },
    ];

    // Check if we're on the ClientProfile1 page
    const isClientProfilePage = location.pathname === "/client/profile";

    // Check if we're on a public profile page (client/profile/{id})
    const isPublicProfilePage = location.pathname.match(/^\/client\/profile\/[^/]+$/);

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
            } else {
            }
        } finally {
            // Get current language from localStorage to maintain language prefix
            const currentLang = localStorage.getItem("i18nextLng");
            if (currentLang && currentLang !== "en") {
                navigate(`/${currentLang}/auth/login`);
            } else {
                navigate("/auth/login");
            }
        }
    };

    const handleNavigateBack = () => {
        if (Number(user?.role?.id) === RoleEnum.client) {
            navigate("/clients");
        } else {
            navigate("/professionals");
        }
    };

    // Close dropdown when clicking outside
    const handleClickOutside = e => {
        if (isLanguageDropdownOpen) {
            setIsLanguageDropdownOpen(false);
        }
    };

    // Add event listener for outside clicks
    React.useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isLanguageDropdownOpen]);

    return (
        <header className="bg-white border-b sticky top-0 z-50 shadow-sm" style={{ textAlign: "center" }}>
            <div className="max-w-7xl mx-auto px-2 sm:px-4">
                <div className="flex justify-between h-14 sm:h-16">
                    {/* Left side controls */}
                    <div className="flex items-center gap-2 sm:gap-4">
                        {/* Menu button - visible only on mobile and tablet (hidden on desktop >= 1024px) */}
                        {!isPublicProfilePage && (
                            <button
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden"
                                aria-label="Open menu">
                                <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        )}
                        {/* Logo */}
                        <button
                            onClick={handleNavigateBack}
                            className="flex items-center gap-1 sm:gap-2 cursor-pointer group">
                            <img
                                src="https://assests.netlify.app/assets/images/logo.png"
                                alt="Kokobeo Logo"
                                className="h-6 w-6 sm:h-8 sm:w-8"
                            />
                            <span className="text-blue-600 text-lg sm:text-xl font-bold group-hover:text-blue-700">
                                Kokobeo
                            </span>
                        </button>
                    </div>

                    {/* Right side controls */}
                    <div className="flex items-center gap-2 sm:gap-4">
                        {/* Language Selector */}
                        <LanguageSelector variant="modal" />

                        {/* Notification bell */}
                        <Button
                            variant="ghost"
                            className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg relative"
                            aria-label="Notifications"
                            onClick={() => navigate("/notifications")}>
                            <Bell className="h-5 w-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile menu slide-in panel */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        {/* Backdrop overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />

                        {/* Conditional menu panel */}
                        {isClientProfilePage ? (
                            <SidebarComponent
                                isMobileMenuOpen={isMobileMenuOpen}
                                setIsMobileMenuOpen={setIsMobileMenuOpen}
                            />
                        ) : (
                            <motion.div
                                initial={{ x: -320 }}
                                animate={{ x: 0 }}
                                exit={{ x: -320 }}
                                transition={{ type: "tween" }}
                                className="fixed left-0 top-0 w-[270px] sm:w-[320px] h-full bg-white z-50 overflow-y-auto border-r border-gray-200 shadow-lg">
                                {/* Menu header */}
                                <div className="sticky top-0 bg-white p-4 border-b border-gray-100">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-semibold text-gray-900">{t("favorites.menu")}</h2>
                                        <button
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                            aria-label="Close menu">
                                            <X className="h-5 w-5 text-gray-600" />
                                        </button>
                                    </div>
                                </div>

                                {/* Menu content */}
                                <div className="p-4">
                                    <nav className="space-y-1">
                                        {MENU_ITEMS.map((item, index) =>
                                            item.path === "/logout" ? (
                                                <button
                                                    key={index}
                                                    onClick={handleLogout}
                                                    className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                                    {item.icon}
                                                    <span>{item.label}</span>
                                                </button>
                                            ) : (
                                                <div key={index} className="flex items-center justify-between p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors text-sm sm:text-base mb-2">
                                                    <Link
                                                        to={item.path}
                                                        className="flex items-center space-x-3 flex-1"
                                                        onClick={() => setIsMobileMenuOpen(false)}>
                                                        {item.icon}
                                                        <span className="font-medium whitespace-normal break-words">
                                                            {item.label}
                                                        </span>
                                                    </Link>
                                                    {item.rightIcon && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                navigate("/");
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            className="p-1 hover:bg-gray-200 rounded transition-colors ml-2">
                                                            {item.rightIcon}
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </nav>

                                    <StoreLinks />
                                    <SocialIcons />
                                </div>
                            </motion.div>
                        )}
                    </>
                )}
            </AnimatePresence>
        </header>
    );
};

export default SharedHeader5;
